import Dispatcher from '../dispatcher';
import API from '../../API/api';
import FittingStationFinderConstants from '../constants/fitting_station_finder_constants';
import resolvePromise from '../helper/resolved_promise';
import PostcodeStore from '../stores/postcode_store';
import FittingStationFinderAnalytics from '../../_modules/google_analytics/fitting_station_finder';
import {
    getCsrfTokenKey,
    getCsrfTokenKeyParamName,
    getCsrfTokenValue,
    getCsrfTokenValueParamName,
    getCsrfTokenQueryParam
} from '../../utilities/csrf_token_provider';
import {
    showAction as lockUi,
    hideAction as unlockUi
} from '../actions/ui_lock_actions';
import Page from '../../_pages/page';
import { fetchCsrfToken } from '../helper/requests/fetch_csrf_token';
import { CrossSellProductStore } from '../stores/cross_sell_product_store';
import { triggerInstantJSAddToCartEvent } from '../../../lib/utilities';

async function setActiveStationAction(payload, updateStationInformation = true, wheel2 = false, wheel4 = false) {
    const { voucherData } = TYROOLA.initialFluxStates;
    Dispatcher.handleActiveStationsAction({
        type: 'SET_ACTIVE_STATION',
        payload,
    });

    if (updateStationInformation) {
        FittingStationFinderAnalytics.sendActiveStationChanged();

        const data = {
            postcode: PostcodeStore.getState().appliedPostcode,
            fittingStationId: payload.id
        };

        if (wheel2) {
            data.wheelAlignmentTwo = 1;
        }
        if (wheel4) {
            data.wheelAlignmentFour = 1;
        }
        
        data.idVouchers = voucherData && voucherData.allDiscounts
            ? voucherData.allDiscounts.map((d) => d.idVoucher)
            : [];

        await fetchCsrfToken();
        data[getCsrfTokenKeyParamName()] = getCsrfTokenKey();
        data[getCsrfTokenValueParamName()] = getCsrfTokenValue();

        return API.GET({
            apiPath: FittingStationFinderConstants.SET_FITTING_STATION_API_URL,
            data
        });
    }
    
    return resolvePromise();
}

function unsetActiveStationAction() {
    Dispatcher.handleActiveStationsAction({
        type: 'UNSET_ACTIVE_STATION',
    });

    return resolvePromise();
}

function fetchInitialStationAction(payload) {
    payload = payload || {};
    Dispatcher.handleActiveStationsAction({ type: 'FETCH_INITIAL_STATION' });
    return _fetchInitialStation(payload).then((station) => {
        Dispatcher.handleActiveStationsAction({
            type: 'INITIAL_STATION_FETCHED',
            payload: station,
        });
    }).catch((unsetInitialStation) => {
        Dispatcher.handleActiveStationsAction({
            type: 'FETCHING_INITIAL_STATION_FAILED',
            payload: unsetInitialStation,
        });
    });
}

async function _fetchInitialStation(data) {
    const initialStationPromise = $.Deferred();
    await fetchCsrfToken();
    data[getCsrfTokenKeyParamName()] = getCsrfTokenKey();
    data[getCsrfTokenValueParamName()] = getCsrfTokenValue();
    API.GET({
        apiPath: FittingStationFinderConstants.GET_PRESELECTED_FITTING_STATION_API_URL,
        data,
        dataType: 'json',
    }).then((station) => {
        if (station.totalExpenses) {
            initialStationPromise.resolve(station);
        } else {
            initialStationPromise.reject(true);
        }
    }).catch(() => {
        initialStationPromise.reject(false);
    });

    return initialStationPromise;
}

function setWheelAlignment(wheelAmount, stationId) {
    Dispatcher.handleActiveStationsAction({
        type: "SET_WHEEL_ALIGNMENT",
        payload: { wheelAmount, stationId }
    });
}

function unsetWheelAlignment(wheelAmount, stationId) {
    Dispatcher.handleActiveStationsAction({
        type: "UNSET_WHEEL_ALIGNMENT",
        payload: { wheelAmount, stationId }
    });
}

function addProductToCartAction(payload, productId, isMpsOrder=false, updateStationInformation = true, wheel2 = false, wheel4 = false) {
    const { voucherData } = TYROOLA.initialFluxStates;
    const crossSellProducts = CrossSellProductStore.getState();
    Dispatcher.handleActiveStationsAction({
        type: 'ADD_MPS_PRODUCT_TO_CART',
        payload,
    });

    if (updateStationInformation) {
        const hasCrossSellProducts = crossSellProducts && crossSellProducts.length;

        const data = {
            id: productId,
            qty: payload.qty,
            fittingStationId: payload.id,
            wheelAlignmentTwo: 0,
            wheelAlignmentFour: 0,
            isMpsOrder: isMpsOrder,
            crossSellProducts: hasCrossSellProducts
                ? crossSellProducts.filter(csp => csp.preselected).map(csp => ({ spinachId: csp.spinachId, cartQty: (csp.syncQty ? (payload.qty * 1) : 1) }))
                : undefined
        };

        if (wheel2) {
            data.wheelAlignmentTwo = 1;
        }
        if (wheel4) {
            data.wheelAlignmentFour = 1;
        }
        
        data.idVouchers = voucherData && voucherData.allDiscounts
            ? voucherData.allDiscounts.map((d) => d.idVoucher)
            : [];
        
        const cartAddPath = `${FittingStationFinderConstants.POST_ADD_TO_CART}?${getCsrfTokenQueryParam()}`;
        lockUi();
        return API.POST({
            apiPath: cartAddPath,
            data: JSON.stringify(data),
            dataType: 'json',
            contentType: 'application/json; charset=utf-8'
        }).then(() => {
            triggerInstantJSAddToCartEvent(data.id, data.qty);
            unlockUi();
        });
    }

    return resolvePromise();
}

class ActiveStationsActions {
    static setActiveStationAction = setActiveStationAction;
    static addProductToCartAction = addProductToCartAction;
    static unsetActiveStationAction = unsetActiveStationAction;
    static fetchInitialStationAction = fetchInitialStationAction;
    static setWheelAlignment = setWheelAlignment;
    static unsetWheelAlignment = unsetWheelAlignment;
}

export {
    setActiveStationAction,
    addProductToCartAction,
    unsetActiveStationAction,
    fetchInitialStationAction,
    setWheelAlignment,
    unsetWheelAlignment
};

export default ActiveStationsActions;
